<template>
  <div id="tags-view-container" class="h-[34px] w-full bg-white border-b border-slate-100 shadow-sm">
    <scroll-pane ref="scrollPaneRef" class="w-full h-full whitespace-nowrap" @scroll="handleScroll">
      <router-link
        v-for="tag in visitedViews"
        :key="tag.path"
        :data-path="tag.path"
        class="inline-flex items-center justify-center h-[26px] mt-1 ml-2 px-2 text-xs border border-slate-200 bg-white text-slate-500 transition-all cursor-pointer first:ml-4 last:mr-4 hover:text-blue-500 hover:border-blue-200 rounded"
        :class="{ 
          'bg-blue-500 !border-transparent !text-white hover:!text-white': isActive(tag) 
        }"
        :to="{ path: tag.path, query: tag.query, fullPath: tag.fullPath }"
        :style="activeStyle(tag)"
        @click.middle="!isAffix(tag) ? closeSelectedTag(tag) : ''"
        @contextmenu.prevent="openMenu(tag, $event)"
      >
        <span 
          v-if="isActive(tag)" 
          class="inline-block w-2 h-2 bg-white rounded-full mr-2"
        ></span>
        
        {{ tag.title }}
        
        <span 
          v-if="!isAffix(tag)" 
          class="ml-1.5 w-4 h-4 rounded-full hover:bg-black/15 hover:text-white transition-colors duration-200 flex items-center justify-center"
          @click.prevent.stop="closeSelectedTag(tag)"
        >
          <close class="w-2.5 h-2.5" />
        </span>
      </router-link>
    </scroll-pane>

    <ul 
      v-show="visible" 
      :style="{ left: left + 'px', top: top + 'px' }" 
      class="fixed z-50 min-w-[100px] m-0 py-1.5 bg-white rounded-md shadow-lg list-none text-xs text-slate-600 border border-slate-100"
    >
      <li 
        class="px-4 py-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 transition-colors"
        @click="refreshSelectedTag(selectedTag)"
      > 
        <refresh-right class="w-3.5 h-3.5" /> 刷新页面 
      </li>
      <li 
        v-if="!isAffix(selectedTag)" 
        class="px-4 py-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 transition-colors"
        @click="closeSelectedTag(selectedTag)"
      >
        <close class="w-3.5 h-3.5" /> 关闭当前
      </li>
      <li 
        class="px-4 py-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 transition-colors"
        @click="closeOthersTags"
      > 
        <circle-close class="w-3.5 h-3.5" /> 关闭其他 
      </li>
      <li 
        v-if="!isFirstView()" 
        class="px-4 py-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 transition-colors"
        @click="closeLeftTags"
      > 
        <back class="w-3.5 h-3.5" /> 关闭左侧 
      </li>
      <li 
        v-if="!isLastView()" 
        class="px-4 py-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 transition-colors"
        @click="closeRightTags"
      > 
        <right class="w-3.5 h-3.5" /> 关闭右侧 
      </li>
      <li 
        class="px-4 py-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 transition-colors border-t border-slate-100 mt-1 pt-2"
        @click="closeAllTags(selectedTag)"
      > 
        <circle-close class="w-3.5 h-3.5" /> 全部关闭 
      </li>
    </ul>
  </div>
</template>

<script setup>
import ScrollPane from './ScrollPane'
import { getNormalPath } from '@/utils/mei-mei'
import useTagsViewStore from '@/store/modules/tagsView'
import useSettingsStore from '@/store/modules/settings'
import usePermissionStore from '@/store/modules/permission'
import { Close, RefreshRight, CircleClose, Back, Right } from '@element-plus/icons-vue'

const visible = ref(false)
const top = ref(0)
const left = ref(0)
const selectedTag = ref({})
const affixTags = ref([])
const scrollPaneRef = ref(null)

const { proxy } = getCurrentInstance()
const route = useRoute()
const router = useRouter()

const visitedViews = computed(() => useTagsViewStore().visitedViews)
const routes = computed(() => usePermissionStore().routes)
const theme = computed(() => useSettingsStore().theme)

watch(route, () => {
  addTags()
  moveToCurrentTag()
})
watch(visible, (value) => {
  if (value) {
    document.body.addEventListener('click', closeMenu)
  } else {
    document.body.removeEventListener('click', closeMenu)
  }
})
onMounted(() => {
  initTags()
  addTags()
})

function isActive(r) {
  return r.path === route.path
}

function activeStyle(tag) {
  if (!isActive(tag)) return {}
  // 如果 settingsStore 中有自定义主题色，则覆盖默认的蓝色
  return {
    'background-color': theme.value,
    'border-color': theme.value,
  }
}

function isAffix(tag) {
  return tag.meta && tag.meta.affix
}
function isFirstView() {
  try {
    return selectedTag.value.fullPath === '/index' || selectedTag.value.fullPath === visitedViews.value[1].fullPath
  } catch (err) {
    return false
  }
}
function isLastView() {
  try {
    return selectedTag.value.fullPath === visitedViews.value[visitedViews.value.length - 1].fullPath
  } catch (err) {
    return false
  }
}
function filterAffixTags(routes, basePath = '') {
  let tags = []
  routes.forEach((route) => {
    if (route.meta && route.meta.affix) {
      const tagPath = getNormalPath(basePath + '/' + route.path)
      tags.push({
        fullPath: tagPath,
        path: tagPath,
        name: route.name,
        meta: { ...route.meta },
      })
    }
    if (route.children) {
      const tempTags = filterAffixTags(route.children, route.path)
      if (tempTags.length >= 1) {
        tags = [...tags, ...tempTags]
      }
    }
  })
  return tags
}
function initTags() {
  const res = filterAffixTags(routes.value)
  affixTags.value = res
  for (const tag of res) {
    if (tag.name) {
      useTagsViewStore().addVisitedView(tag)
    }
  }
}
function addTags() {
  const { name } = route
  if (name) {
    useTagsViewStore().addView(route)
    if (route.meta.link) {
      useTagsViewStore().addIframeView(route)
    }
  }
  return false
}
function moveToCurrentTag() {
  nextTick(() => {
    for (const r of visitedViews.value) {
      if (r.path === route.path) {
        scrollPaneRef.value.moveToTarget(r)
        if (r.fullPath !== route.fullPath) {
          useTagsViewStore().updateVisitedView(route)
        }
      }
    }
  })
}
function refreshSelectedTag(view) {
  proxy.$tab.refreshPage(view)
  if (route.meta.link) {
    useTagsViewStore().delIframeView(route)
  }
}
function closeSelectedTag(view) {
  proxy.$tab.closePage(view).then(({ visitedViews }) => {
    if (isActive(view)) {
      toLastView(visitedViews, view)
    }
  })
}
function closeRightTags() {
  proxy.$tab.closeRightPage(selectedTag.value).then((visitedViews) => {
    if (!visitedViews.find((i) => i.fullPath === route.fullPath)) {
      toLastView(visitedViews)
    }
  })
}
function closeLeftTags() {
  proxy.$tab.closeLeftPage(selectedTag.value).then((visitedViews) => {
    if (!visitedViews.find((i) => i.fullPath === route.fullPath)) {
      toLastView(visitedViews)
    }
  })
}
function closeOthersTags() {
  router.push(selectedTag.value).catch(() => {})
  proxy.$tab.closeOtherPage(selectedTag.value).then(() => {
    moveToCurrentTag()
  })
}
function closeAllTags(view) {
  proxy.$tab.closeAllPage().then(({ visitedViews }) => {
    if (affixTags.value.some((tag) => tag.path === route.path)) {
      return
    }
    toLastView(visitedViews, view)
  })
}
function toLastView(visitedViews, view) {
  const latestView = visitedViews.slice(-1)[0]
  if (latestView) {
    router.push(latestView.fullPath)
  } else {
    if (view.name === 'Dashboard') {
      router.replace({ path: '/redirect' + view.fullPath })
    } else {
      router.push('/')
    }
  }
}
function openMenu(tag, e) {
  const menuMinWidth = 105
  const offsetLeft = proxy.$el.getBoundingClientRect().left
  const offsetWidth = proxy.$el.offsetWidth
  const maxLeft = offsetWidth - menuMinWidth
  const l = e.clientX - offsetLeft + 15

  if (l > maxLeft) {
    left.value = maxLeft
  } else {
    left.value = l
  }

  top.value = e.clientY
  visible.value = true
  selectedTag.value = tag
}
function closeMenu() {
  visible.value = false
}
function handleScroll() {
  closeMenu()
}
</script>